import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useShop } from '@/hooks/useShop';
import { useIsFirstSession } from '@/hooks/useIsFirstSession';
import { useSubscribe } from '@/hooks/useSubscribe';
import { useMobileNotice } from '@/lib/mobileNotice';
import { MobileInterstitial } from '@/components/mobile/MobileInterstitial';
import { Loader } from '@/components/ui/loader';
import { ToastProvider, useToast } from '@/components/ui/toast';
import unifyLogo from '@/assets/unify-logo.png';

import { ReadyToLaunchHero } from './ReadyToLaunchHero';
import { WelcomeNavHint } from './WelcomeNavHint';

/**
 * Dedicated first-login welcome route. Rendered outside `AppLayout` on its own
 * minimal layout — Unify wordmark + the ready-to-launch hero, no sidebar, nav
 * or shop switcher.
 *
 * Self-protecting: only genuine first-session users see it. Returning users
 * (already dismissed) and HQ staff impersonating a retailer are redirected to
 * the dashboard root, which keeps direct navigation to `/welcome` safe.
 *
 * Has its own `ToastProvider` because it lives outside the shell that normally
 * supplies one.
 */
export function Welcome() {
	return (
		<ToastProvider>
			<WelcomeContent />
		</ToastProvider>
	);
}

function WelcomeContent() {
	const { isFirstSession, isResolving, dismiss } = useIsFirstSession();
	const { subscribe, isMigrationCohort } = useSubscribe();
	const { shop } = useShop();
	const { toast } = useToast();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	// NEA-5932 — show the "best on desktop" interstitial *before* the Let's go
	// CTA, so mobile users see the warning while still in first-session state.
	// If they open on desktop instead, they re-land on /welcome (`dismiss()`
	// hasn't been called) and see the onboarding intro there.
	const { shouldShow: showMobileNotice } = useMobileNotice();

	if (isResolving) return <Loader label="Loading…" />;
	if (!isFirstSession) return <Navigate to="/" replace />;

	const handleGetStarted = async () => {
		setLoading(true);
		try {
			// Migration cohort subscribes here (always Pro — the server ignores any
			// plan and runs the migration journey). Evergreen customers who arrived
			// via the marketing site with `?plan=starter` or `?plan=pro` (stored on
			// `shop.plan` at create-shop time) auto-subscribe to that tier — Lovable
			// substitutes for the in-app plan picker. Anything else — `unknown-plan`
			// for organic signups that skipped the pricing cards, or legacy
			// `unify-1`..`unify-4` on shops created before the cutover — falls
			// through to dismiss-only.
			if (isMigrationCohort) {
				await subscribe();
			} else if (shop?.plan === 'starter' || shop?.plan === 'pro') {
				await subscribe(shop.plan);
			}
			await dismiss();
			// Hand off to the first-visit guided tour: IntroTour (mounted in
			// AppLayout) auto-starts on the dashboard when this flag is set.
			try {
				window.localStorage.setItem('unify.tour-pending', '1');
			} catch {
				/* tour is best-effort */
			}
			navigate('/', { replace: true });
		} catch {
			setLoading(false);
			toast({
				variant: 'destructive',
				title: 'Something went wrong',
				description: "We couldn't get you started just then. Please try again."
			});
		}
	};

	return (
		<main className="relative flex min-h-screen overflow-hidden bg-background">
			{showMobileNotice ? <MobileInterstitial /> : null}
			<WelcomeNavHint shopName={shop?.name} />

			<div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
				<div className="pointer-events-none absolute inset-0 bg-gradient-soft opacity-60" />
				<div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-gradient-unify opacity-20 blur-3xl" />
				<div className="pointer-events-none absolute -bottom-20 -right-20 h-80 w-80 rounded-full bg-gradient-unify opacity-20 blur-3xl" />

				<header className="relative z-10 px-6 py-6 md:px-10">
					<img src={unifyLogo} alt="Unify by NearSt" className="h-10 w-auto" />
				</header>

				<div className="relative z-10 flex flex-1 items-center justify-center px-6 pb-20">
					<ReadyToLaunchHero name={shop?.name || ''} onGetStarted={handleGetStarted} loading={loading} />
				</div>
			</div>
		</main>
	);
}
